/**
 * ObligationSummaryPanel.tsx — V85-1-8.
 *
 * Fetches the two-register obligation summary payload
 * (`GET /api/w/:slug/systems/:systemId/obligations/summary`) once and
 * renders it through both registers built in V6-6-7:
 *   - ObligationSummaryProduct — plain-language "what you must do"
 *   - ObligationSummaryCited   — same data with citations behind a
 *     "verify" affordance, for the auditor/legal reader
 *
 * Mounted on SystemDetail's requirements tab and the Plan lifecycle
 * page. Citations never render inline by default — the reader opts
 * into the cited register, and even there each citation sits behind
 * its own "Verify source" toggle (ObligationSummaryCited).
 */

import { useState } from 'react';
import { Alert, Button, Spinner } from '@aigovops/design-system';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys';
import { getObligationSummary, downloadObligationSummaryPdf } from '../lib/api';
import { ObligationSummaryProduct } from './ObligationSummaryProduct';
import { ObligationSummaryCited } from './ObligationSummaryCited';

type Register = 'product' | 'cited';

interface ObligationSummaryPanelProps {
  workspaceSlug: string;
  systemId: string;
}

export function ObligationSummaryPanel({ workspaceSlug, systemId }: ObligationSummaryPanelProps) {
  const [register, setRegister] = useState<Register>('product');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const summaryQuery = useQuery({
    queryKey: queryKeys.obligationSummary(workspaceSlug, systemId),
    queryFn: () => getObligationSummary(workspaceSlug, systemId),
    enabled: !!workspaceSlug && !!systemId,
  });
  const data = summaryQuery.data ?? null;
  const loading = summaryQuery.isLoading;
  const error = summaryQuery.error?.message ?? null;
  const refetch = () => { summaryQuery.refetch(); };

  const onDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await downloadObligationSummaryPdf(workspaceSlug, systemId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `obligation-summary-${systemId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'The PDF could not be generated.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        Obligation summary could not be loaded. {error}{' '}
        <Button variant="ghost" onClick={refetch}>Retry</Button>
      </Alert>
    );
  }

  const payload = data && data.obligation_set_id
    ? { obligation_set_id: data.obligation_set_id, system_id: data.system_id, obligations: data.obligations }
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <div role="tablist" aria-label="Obligation register" style={{ display: 'flex', gap: '4px' }}>
          <Button
            variant={register === 'product' ? 'primary' : 'ghost'}
            compact
            role="tab"
            aria-selected={register === 'product'}
            onClick={() => setRegister('product')}
          >
            What you must do
          </Button>
          <Button
            variant={register === 'cited' ? 'primary' : 'ghost'}
            compact
            role="tab"
            aria-selected={register === 'cited'}
            onClick={() => setRegister('cited')}
          >
            Why (citations)
          </Button>
        </div>
        {payload && (
          <Button variant="ghost" compact onClick={onDownload} aria-disabled={downloading}>
            {downloading ? 'Preparing PDF…' : 'Download PDF'}
          </Button>
        )}
      </div>

      {downloadError && <Alert variant="error">{downloadError}</Alert>}

      {register === 'product' ? (
        <ObligationSummaryProduct payload={payload} />
      ) : (
        <ObligationSummaryCited payload={payload} />
      )}
    </div>
  );
}
